import { createFileRoute } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import CardState from '@/components/ui/CardState'
import SectionCard from '@/components/ui/SectionCard'
import SeeAllLink from '@/components/ui/SeeAllLink'
import { usePortfolioSummary } from '@/features/portfolio/api/queries'
import { useWatchlistCollection } from '@/features/watchlist/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { resolveViewState } from '@/lib/view-state'
import BasketsToTryCard from '@/screens/app/home/components/BasketsToTryCard'
import CompanyEventsCard from '@/screens/app/home/components/CompanyEventsCard'
import EmptyPortfolioCard from '@/screens/app/home/components/EmptyPortfolioCard'
import EmptyWatchlistNudge from '@/screens/app/home/components/EmptyWatchlistNudge'
import HomeMoversCard from '@/screens/app/home/components/HomeMoversCard'
import HomeRail from '@/screens/app/home/components/HomeRail'
import { HomeSkeleton } from '@/screens/app/home/components/HomeSkeletons'
import IndexTodayCard from '@/screens/app/home/components/IndexTodayCard'
import LatestFromMoneyMitraCard from '@/screens/app/home/components/LatestFromMoneyMitraCard'
import ObjectivesCard from '@/screens/app/home/components/ObjectivesCard'
import OpenIssuesCard from '@/screens/app/home/components/OpenIssuesCard'
import PortfolioCard from '@/screens/app/home/components/PortfolioCard'
import TodaysBriefCard from '@/screens/app/home/components/TodaysBriefCard'
import TodaysNewsCard from '@/screens/app/home/components/TodaysNewsCard'
import { useIsFirstRun } from '@/screens/app/home/lib/useIsFirstRun'
import { loadHome } from '@/screens/app/HomeScreen.loader'

/**
 * One page-wide skeleton, in the loaded column's order and at its heights,
 * while the loader settles the reads that decide Home's shape. No
 * `errorComponent`: the loader never throws for its own reads (see its doc
 * comment), so anything that reaches the router's default is a real fault.
 *
 * Home's own rail, the same as the loaded page's: the rail is frame, and it
 * carries on through the handoff rather than entering again, so a pending
 * page drawing the shared rail would swap it for HomeRail the moment the
 * content landed. HomeRail is the shared rail's own cards plus the
 * watchlist block, so the entry chunk takes on next to nothing for it.
 */
function HomePending() {
  return (
    <ScreenLayout pending pendingLabel="Loading your home page…" rail={<HomeRail />}>
      <HomeSkeleton />
    </ScreenLayout>
  )
}

export const Route = createFileRoute('/_app/')({
  loader: loadHome,
  pendingComponent: HomePending,
  component: HomeScreen,
})

interface WatchlistUnavailableCardProps {
  state: 'failed' | 'offline'
  onRetry: () => void
  retrying: boolean
}

/**
 * Neither of the slot's two real answers can be honest about a watchlist
 * nobody managed to read: the card needs rows it does not have, and the nudge
 * would report "Nothing followed yet" about a list that may well be full. So
 * the slot says the read failed, in the main column where the empty-watchlist
 * nudge would have drawn — the same shape every other Home card gives its own
 * failure (`TodaysNewsCard`, `HomeMoversCard`) — rather than vanishing and
 * leaving the member to guess which of the two it was. No frame draws this
 * state; there is no rail equivalent to fail toward instead, since the rail's
 * own watchlist block reads the identical query and simply renders nothing
 * on any non-loaded answer (see `HomeRail`).
 */
function WatchlistUnavailableCard({ state, onRetry, retrying }: WatchlistUnavailableCardProps) {
  return (
    <SectionCard
      title="Your watchlist"
      action={<SeeAllLink to="/watchlist" aria-label="See your watchlist" />}
    >
      <CardState
        state={state}
        onRetry={onRetry}
        retrying={retrying}
        failedTitle="Your watchlist did not load"
        failedDescription="The watchlist is not answering. Nothing has been removed."
      />
    </SectionCard>
  )
}

/**
 * Home — the pages revamp's order (2026-10-27).
 *
 * ── The column ─────────────────────────────────────────────────────────────
 *
 * NEPSE today · the brief · [the empty-watchlist card] · Gainers and losers ·
 * [Your portfolio, or Track your portfolio here] · Today's news beside
 * Company events · IPOs, rights and other issues · [the first-run strip]. No
 * title: Home is where the shell lands, and it is no section's page, so no
 * strip either. No ask bar: the Ask Mitra bar at the column's foot carries it
 * on every screen. The watchlist's own preview lives in `HomeRail`; only the
 * empty-watchlist card and its failure twin live here, since neither has
 * anywhere else designed for them. `HomeSkeleton` draws the same order.
 *
 * The mock draws three states of this one column (Home redesign, 2026-10-28):
 *
 *   personalised     the member has a book: Your portfolio; the watchlist's rows
 *                    sit in the rail. No nudge, no strip.
 *   empty watchlist  nothing followed: the empty-watchlist card under the brief.
 *   first run        no book AND nothing followed (`useIsFirstRun`): Track your
 *                    portfolio here, and "New here?" last, with NO empty-watchlist
 *                    card, since the mock never stacks the two. Dismiss the strip
 *                    and the same member is in the empty-watchlist state.
 *
 * Baskets to try (after the news pair) reads the live baskets list and is left
 * out entirely while no basket is published. Latest from MoneyMitra (after the
 * issues) reads the live posts feed.
 *
 * ── Two gates, not one (f02 · f03) ──────────────────────────────────────────
 *
 * The stack is chosen by TWO independent reads:
 *
 *   the portfolio slot  whether the member has a priced portfolio.
 *                        `usePortfolioSummary()` already narrows an empty book
 *                        to `null` (see that hook's own doc comment), so
 *                        `null` IS "no portfolio" — nothing here re-derives it.
 *                        Read through `resolveViewState` rather than as a bare
 *                        `data != null`, because the slot needs the PENDING
 *                        answer apart from the negative one; see below.
 *   the watchlist slot   whether the watchlist has rows. Nothing else.
 *
 * The pairing "watchlist populated, no portfolio" is left unspecified — no
 * frame draws it — and an earlier revision of this screen closed the gap by
 * folding the watchlist into the portfolio gate ("personalised = has a portfolio,
 * watchlist state irrelevant"). That was wrong in the one way it could be: a
 * member following names but holding no stock was shown "Nothing followed
 * yet" over a watchlist that was not empty. The owner has since ruled the
 * other way, and the card is about the watchlist, so the watchlist decides.
 * The two gates can now produce a pairing no captured frame shows — the rail's
 * watchlist block drawn, "Track your portfolio here" in the main column — and
 * that is the point of splitting them.
 *
 * ── Both slots are decided before the first frame ──────────────────────────
 *
 * The route's loader (`HomeScreen.loader.ts`) awaits both reads — settled,
 * success or failure — before the page commits, so each gate below already
 * has its real answer on the first render and neither slot appears, grows or
 * collapses under the member afterwards. That replaced two compromises this
 * comment used to defend:
 *
 *   - the portfolio slot, gated on `data != null`, arrived whole when the
 *     summary landed and shoved the column down — Lighthouse (localhost:5174,
 *     2026-09-24) scored CLS 0.198, all of it on the block directly below, a
 *     ~430-470px jump. Holding the slot open on a skeleton fixed that for a
 *     member with a book and moved the same jump onto a member without one,
 *     whose skeleton then resolved to nothing;
 *   - the watchlist slot drew nothing while loading, because its resolutions
 *     share no honest placeholder, and then pushed everything below it down
 *     when one landed.
 *
 * The `loading` branches are still here, drawn the old way, for a read that
 * is somehow pending at mount (a cache cleared under a mounted page). The
 * loader makes that the exception rather than the first frame.
 *
 * `empty` draws `<EmptyPortfolioCard>` in the slot — the two ways to start a
 * book. `failed` and `offline` still draw nothing: a member with no book must
 * not be told their portfolio "did not load", and PortfolioCard's own failure
 * copy would say exactly that; nor offered a first book on the evidence of a
 * request that never answered.
 *
 * ── What the watchlist slot draws in the MAIN column, in every state ───────
 *
 *   loaded     nothing — `HomeRail`'s own watchlist block reads the same
 *              cache entry and draws the rows instead ("the watchlist
 *              moving to the rail").
 *   empty      `<EmptyWatchlistNudge>`, which keeps its own dismissal
 *              (`localStorage`, no preferences endpoint exists yet to
 *              hold it server-side) and re-derives this same emptiness
 *              predicate off this same cache entry — so the two can never
 *              disagree, and it stays a component that works on its own.
 *   failed /   `<WatchlistUnavailableCard>`; see its own note. Falling back to
 *   offline    the nudge would report an empty watchlist on the evidence of a
 *              request that never answered.
 *   loading    nothing.
 *
 * Reading the collection here costs no extra request: `useWatchlistCollection`
 * is a bare `useQuery` on a constant key (`watchlistKeys.collection()`), so
 * this call, `HomeRail`'s own call and the nudge's own call are three
 * observers of ONE cache entry — the one the loader filled. That is React
 * Query's own dedup, underneath `src/api/endpointCache.ts`, and it is why
 * `WatchlistScreen`, `SidebarSections`, `AlertsScreen` and `StockScreen` have
 * always been able to read it side by side.
 */
function HomeScreen() {
  const online = useOnline()

  const portfolioSummary = usePortfolioSummary()
  const portfolioState = resolveViewState(portfolioSummary, {
    isEmpty: (summary) => summary === null,
    online,
  })
  // `loaded` fills the slot and `loading` reserves it; `empty` swaps in the
  // start-a-book card, and `failed`/`offline` draw nothing. See "Both slots
  // are decided" in the doc comment.
  const showPortfolio = portfolioState === 'loaded' || portfolioState === 'loading'

  const watchlist = useWatchlistCollection()
  const watchlistState = resolveViewState(watchlist, {
    isEmpty: (data) => data.lists.every((list) => list.rows.length === 0),
    online,
  })

  // First run draws "New here?" and withholds the empty-watchlist card (see the doc comment).
  const firstRun = useIsFirstRun()

  // No `header`: every other screen names itself because a member navigated
  // into that section, but Home is where the shell lands by default. Owner's
  // call.
  return (
    <ScreenLayout rail={<HomeRail />}>
      <IndexTodayCard />
      <TodaysBriefCard />
      {watchlistState === 'empty' && !firstRun ? <EmptyWatchlistNudge /> : null}
      {watchlistState === 'failed' || watchlistState === 'offline' ? (
        <WatchlistUnavailableCard
          state={watchlistState}
          onRetry={() => {
            void watchlist.refetch()
          }}
          retrying={watchlist.isFetching}
        />
      ) : null}
      <HomeMoversCard />
      {showPortfolio ? <PortfolioCard /> : null}
      {portfolioState === 'empty' ? <EmptyPortfolioCard /> : null}
      {/* Side by side from `@2xl` (672px) of the column, as Gainers and losers
          splits: below it two ~320px panels would cut every headline short. */}
      <div className="grid gap-6 @2xl:grid-cols-2">
        <TodaysNewsCard />
        <CompanyEventsCard />
      </div>
      <BasketsToTryCard />
      <OpenIssuesCard />
      <LatestFromMoneyMitraCard />
      <ObjectivesCard />
    </ScreenLayout>
  )
}
